import { useCallback, useEffect, useState } from "react";
import { api } from "./api";
import { useAuth } from "../context/AuthContext";

/**
 * Phase 218 — Company Policies: shared client helpers.
 *  • usePolicyAccess(code)  -> my rights + my_total / my_pending (cached per signed-in account so every shell /
 *                              layout can decide whether to show the "Policies" / "My Policies" menu without refetching)
 *  • invalidatePolicyAccess -> after signing / uploading / changing permissions
 *  • fetchPolicyPdfUrl      -> authenticated blob URL for the inline PDF viewer
 */

const store = { key: null, data: null, promise: null, last: null, listeners: new Set() };

function notify() { store.listeners.forEach((fn) => { try { fn(store.data); } catch { /* ignore */ } }); }

/**
 * Drop the cached answer. Every mounted hook keeps showing its last data (no menu flicker) and is refreshed as soon
 * as the fresh answer arrives — so signing a policy or changing permissions updates the menus in place.
 */
export function invalidatePolicyAccess() {
  const last = store.last;
  store.key = null;
  store.data = null;
  store.promise = null;
  if (last && store.listeners.size > 0) load(last.code, last.uid);
}

/** Forget everything (login / logout) — no refetch. */
export function clearPolicyAccess() {
  store.key = null;
  store.data = null;
  store.promise = null;
  store.last = null;
}

function load(code, uid) {
  const key = `${code}:${uid}`;
  if (store.key === key && store.data) return Promise.resolve(store.data);
  if (store.key === key && store.promise) return store.promise;
  store.key = key;
  store.last = { code, uid };
  store.data = null;
  store.promise = api.get(`/t/${code}/policies/access`)
    .then((r) => { if (store.key === key) { store.data = r.data; notify(); } return r.data; })
    .catch(() => { const empty = { library_visible: false, my_total: 0, my_pending: 0, can_view: false }; if (store.key === key) { store.data = empty; notify(); } return empty; })
    .finally(() => { if (store.key === key) store.promise = null; });
  return store.promise;
}

/** Rights + counts of the signed-in staff member. `loaded` is false until the first answer arrives. */
export function usePolicyAccess(code) {
  const { auth } = useAuth();
  const uid = auth?.user?.id || null;
  const role = auth?.user?.role;
  const staff = !!uid && role && role !== "citizen" && role !== "super_admin";
  const key = `${code}:${uid}`;
  const [data, setData] = useState(() => (store.key === key ? store.data : null));
  useEffect(() => {
    if (!code || !staff) return undefined;
    const fn = (d) => { if (d) setData(d); };   // keep the last answer while a refresh is in flight
    store.listeners.add(fn);
    load(code, uid).then((d) => setData(d));
    return () => { store.listeners.delete(fn); };
  }, [code, uid, staff]);
  const reload = useCallback(() => { if (code && staff) { store.key = null; store.data = null; store.promise = null; load(code, uid).then((d) => setData(d)); } }, [code, uid, staff]);
  return {
    loaded: !staff || data !== null,
    access: data,
    libraryVisible: !!data?.library_visible,
    myTotal: data?.my_total || 0,
    myPending: data?.my_pending || 0,
    reload,
  };
}

/** Raw PDF blob of a policy (current version by default). */
export async function fetchPolicyPdfBlob(code, policyId, version) {
  const r = await api.get(`/t/${code}/policies/${policyId}/file`, { params: version ? { version } : {}, responseType: "blob" });
  return r.data.type === "application/pdf" ? r.data : new Blob([r.data], { type: "application/pdf" });
}

/** Blob URL of a policy PDF (current version by default). Caller revokes it with URL.revokeObjectURL. */
export async function fetchPolicyPdfUrl(code, policyId, version) {
  return URL.createObjectURL(await fetchPolicyPdfBlob(code, policyId, version));
}

export const ROLE_LABELS = { municipal_admin: "Municipal Admin", supervisor: "Supervisor", tax_collector: "Tax Collector", parking_marshal: "Parking Marshal" };
